import React, { useEffect, useState } from "react";
import useNumericInput from "../../../hooks/useNumericInput";
import { useLoader } from "../../../utils/LoaderUtils";
import { showError, showSuccess } from "../../../utils/toastUtils";
import { useParams } from "react-router-dom";
import CouponAPI from "../../../api/promo/couponApi";
import { handleApiValidationError } from "../../../utils/apiErrorHandler";

function Edit() {
  useNumericInput();

  const location = useParams();
  const id = location.id;

  const today = new Date().toISOString().split("T")[0];
  const [errors, setErrors] = useState({});
  const { showLoader, hideLoader } = useLoader();
  const [isLoading, setIsLoading] = useState(false);

  const [formData, setFormData] = useState({
    title: "",
    code: "",
    discountType: "PERCENTAGE",
    discountValue: "",
    maximumDiscount: "",
    minimumCartValue: "",
    startDate: "",
    endDate: "",
    usageLimit: "",
    usageLimitPerPerson: "",
    description: "",
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.title) {
      newErrors.title = "Title is required";
    }

    if (!formData.code) {
      newErrors.code = "Code is required";
    }

    if (!formData.usageLimit) {
      newErrors.usageLimit = "Usage Limit is required";
    }

    if (!formData.maximumDiscount) {
      newErrors.maximumDiscount = "Maximum Discount is required";
    }

    if (!formData.minimumCartValue) {
      newErrors.minimumCartValue = "Minimum Cart Value is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    fetchCouponDetails();
  }, []);

  const fetchCouponDetails = async () => {
    const res = await CouponAPI.getSingleDetails(id);
    if (res.code === 200) {
      const result = res.data;

      setFormData({
        title: result.title || "",
        code: result.code || "",
        discountType: result.discountType || "",
        discountValue: result.discountValue || "",
        maximumDiscount: result.maximumDiscount || "",
        minimumCartValue: result.minimumCartValue || "",
        startDate: result.startDate || "",
        endDate: result.endDate || "",
        description: result.description || "",
        usageLimit: result.usageLimit || "",
        usageLimitPerPerson: result.usageLimitPerUser || "",
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // if (!validate()) {
    //   return;
    // }

    setIsLoading(true);
    showLoader();

    try {
      const res = await CouponAPI.updateDetails(id, formData);

      if (res.code === 200) {
        showSuccess(res.message);
      } else {
        showError(res.message);
      }
    } catch (err) {
      handleApiValidationError(err, setErrors);
    } finally {
      setIsLoading(false);
      hideLoader();
    }
  };

  return (
    <>
      <div className="content">
        <div className="page-header">
          <div className="page-title">
            <h4>Create New Coupon</h4>
            <h6>
              Set up discount codes with custom rules and restrictions to boost
              your sales.
            </h6>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h4>Add Coupon Details</h4>
          </div>
          <div className="card-body profile-body">
            <form method="post" onSubmit={handleSubmit}>
              <h5 className="mb-1">Basic Information</h5>
              <div className="row mb-3">
                <div className="col-lg-3 col-sm-6">
                  <label className="form-label">
                    Coupon Title
                    <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="Title (e.g., Diwali Offer)"
                    className="form-control"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                  />
                  {errors.title && (
                    <span className="errors text-danger">{errors.title}</span>
                  )}
                </div>
              </div>

              <h5 className="mb-1">Discount Information</h5>
              <div className="row">
                <div className="col-lg-3 col-sm-6">
                  <div className="mb-3">
                    <label className="form-label">
                      Coupon Code <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="Code (e.g., DIWALI10)"
                      className="form-control only-username"
                      name="code"
                      value={formData.code}
                      onChange={handleChange}
                    />
                  </div>
                  {errors.code && (
                    <span className="errors text-danger">{errors.code}</span>
                  )}
                </div>
                <div className="col-lg-3 col-sm-6">
                  <div className="mb-3">
                    <label className="form-label">
                      Discount Type <span className="text-danger">*</span>
                    </label>
                    <select
                      className="form-control"
                      name="discountType"
                      value={formData.discountType}
                      onChange={(e) =>
                        setFormData({
                          discountType: e.target.value,
                          discountValue: "", // reset on change
                        })
                      }
                    >
                      <option value="PERCENTAGE">Percentage (%)</option>
                      <option value="FLAT">Flat Amount (₹)</option>
                    </select>
                  </div>
                </div>

                <div className="col-lg-3 col-sm-6">
                  <div className="mb-3">
                    <label className="form-label">
                      {formData.discountType === "PERCENTAGE"
                        ? "Percentage (%)"
                        : "Flat Discount (₹)"}{" "}
                      <span className="text-danger">*</span>
                    </label>

                    <input
                      type="number"
                      className="form-control"
                      placeholder={
                        formData.discountType === "PERCENTAGE"
                          ? "e.g. 10"
                          : "e.g. 500"
                      }
                      name="discountValue"
                      value={formData.discountValue || ""}
                      onChange={(e) => {
                        const val = e.target.value;

                        // percentage validation
                        if (formData.discountType === "PERCENTAGE") {
                          if (val === "" || (val >= 1 && val <= 100)) {
                            setFormData((p) => ({ ...p, discountValue: val }));
                          }
                        }
                        // flat amount
                        else {
                          if (val === "" || val >= 1) {
                            setFormData((p) => ({ ...p, discountValue: val }));
                          }
                        }
                      }}
                    />
                  </div>
                  {errors.discountValue && (
                    <span className="errors text-danger">
                      {errors.discountValue}
                    </span>
                  )}
                </div>

                <div className="col-lg-3 col-sm-6">
                  <div className="mb-3">
                    <label className="form-label">
                      Maximum Discount Amount
                    </label>
                    <input
                      type="text"
                      placeholder="Amount (e.g., 100)"
                      className="form-control only-numeric"
                      name="maximumDiscount"
                      value={formData.maximumDiscount}
                      onChange={handleChange}
                    />
                  </div>
                  {errors.maximumDiscount && (
                    <span className="errors text-danger">
                      {errors.maximumDiscount}
                    </span>
                  )}
                </div>
              </div>
              <div className="row">
                <div className="col-lg-3 col-sm-6">
                  <div className="mb-3">
                    <label className="form-label">Minimum Cart Value</label>
                    <input
                      type="text"
                      placeholder="Value (e.g., 500)"
                      className="form-control only-numeric"
                      name="minimumCartValue"
                      value={formData.minimumCartValue}
                      onChange={handleChange}
                    />
                  </div>
                  {errors.minimumCartValue && (
                    <span className="errors text-danger">
                      {errors.minimumCartValue}
                    </span>
                  )}
                </div>
              </div>

              <h5 className="mb-1">Valid Information</h5>
              <div className="row">
                <div className="col-md-3">
                  <div className="mb-3">
                    <label className="form-label">
                      Start Date <span className="text-error">*</span>
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      name="startDate"
                      min={today}
                      value={
                        formData.startDate
                          ? formData.startDate.split("T")[0]
                          : today
                      }
                      onChange={handleChange}
                    />
                  </div>
                  {errors.startDate && (
                    <span className="errors text-danger">
                      {errors.startDate}
                    </span>
                  )}
                </div>
                <div className="col-md-3">
                  <div className="mb-3">
                    <label className="form-label">
                      End Date <span className="text-error">*</span>
                    </label>
                    <input
                      type="date"
                      className="form-control"
                      value={
                        formData.endDate
                          ? formData.endDate.split("T")[0]
                          : today
                      }
                      min={formData.startDate || today}
                      name="endDate"
                      onChange={handleChange}
                    />
                  </div>
                  {errors.endDate && (
                    <span className="errors text-danger">{errors.endDate}</span>
                  )}
                </div>
                <div className="col-lg-3">
                  <div className="mb-3">
                    <label className="form-label">
                      Usage Limit <span className="text-error">*</span>
                      {formData.usageLimitCheck && (
                        <span className="text-danger">*</span>
                      )}
                    </label>
                    <input
                      type="text"
                      placeholder="Value (e.g., 1000)"
                      className="form-control only-numeric"
                      name="usageLimit"
                      value={formData.usageLimit}
                      onChange={handleChange}
                    />
                    {errors.usageLimit && (
                      <span className="text-danger errors">
                        {errors.usageLimit}
                      </span>
                    )}
                  </div>
                </div>
                <div className="col-lg-3">
                  <div className="mb-3">
                    <label className="form-label">Usage Limit Per User</label>
                    <input
                      type="text"
                      placeholder="Value (e.g., 1)"
                      className="form-control only-numeric"
                      name="usageLimitPerPerson"
                      value={formData.usageLimitPerPerson}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-12">
                  <label className="form-label">Description</label>
                  <textarea
                    className="form-control"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                  ></textarea>
                  {errors.description && (
                    <span className="text-danger errors">
                      {errors.description}
                    </span>
                  )}
                </div>
              </div>

              <div className="row mt-3">
                <div className="col-12 d-flex justify-content-end">
                  {!isLoading && (
                    <button
                      type="submit"
                      id="submitBtn"
                      className="btn btn-primary shadow-none"
                    >
                      Submit Details
                    </button>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}

export default Edit;
